<template>
    <div class="durationFilterPanel" style="background-color: white;">
        <div v-for="(item,i) in rules" :key="i">
            <div class="durationRuleRow">
                <div class="row items-center no-wrap q-col-gutter-xs">
                    <div class="col-12 col-sm-6">
                        <q-select outlined dense hide-bottom-space
                                :label="Translate('Operator')"
                                :options="condition_types"
                                option-label="text"
                                option-value="value"
                                emit-value
                                map-options
                                v-model="item.condition"
                                @update:model-value="updateReport"
                                bg-color="white" />
                    </div>

                    <div class="col-12 col-sm-6" v-if="item.condition != 'inRange'">
                        <q-input type="number" :label="Translate('Value')"
                                outlined dense hide-bottom-space :placeholder="placeholder"
                                v-model.number="item.value"
                                :disable="item.condition == 'missing'"
                                @update:model-value="updateReport"
                                bg-color="white"
                                class="aligned-spin-buttons">
                            <template v-slot:append>
                                <span class="text-grey-10">{{ units }}</span>
                            </template>
                        </q-input>
                    </div>

                    <div class="col-12 col-sm-3" v-if="item.condition == 'inRange'">
                        <q-input type="number" :label="Translate('From')"
                                outlined dense hide-bottom-space :placeholder="placeholder"
                                v-model.number="item.value"
                                @update:model-value="updateReport"
                                bg-color="white"
                                class="aligned-spin-buttons">
                            <template v-slot:append>
                                <span class="text-grey-10">{{ units }}</span>
                            </template>
                        </q-input>
                    </div>
                    <div class="col-12 col-sm-3" v-if="item.condition == 'inRange'">
                        <q-input type="number" :label="Translate('To')"
                                outlined dense hide-bottom-space :placeholder="placeholder"
                                v-model.number="item.valueto"
                                @update:model-value="updateReport"
                                bg-color="white"
                                class="aligned-spin-buttons">
                            <template v-slot:append>
                                <span class="text-grey-10">{{ units }}</span>
                            </template>
                        </q-input>
                    </div>

                </div>

                <q-btn v-if="i > 0" size="md" text-color="grey-7" flat round dense icon="mdi-window-close" class="durationRemoveBtn" @click="removeCondition()" />
            </div>
            <div class="row justify-center" v-if="i < 1" v-show="rules.length > 1">
                <div class="q-my-sm row items-center spacing-xs">
                    <q-radio class="q-mx-xs" v-model="item.combiner" val="AND" :label="Translate('AND')" @update:model-value="updateReport" />
                    <q-radio class="q-mx-xs" v-model="item.combiner" val="OR" :label="Translate('OR')" @update:model-value="updateReport" />
                </div>
            </div>
        </div>
        <div>
            <q-btn v-show="rules.length < 2" flat dense no-caps unelevated class="q-px-sm q-my-sm customHoverButton c-SentenceCaseButton" @click="addCondition()">
                <q-icon name="mdi-plus" size="xs" class="q-mr-xs" />
                <span>{{ Translate('Add Rule') }}</span>
            </q-btn>
        </div>
    </div>
</template>

<script>
    import { appSettings } from '@/Shared/appSettings';
    import methods from '@/Shared/methods';

    import _ from 'lodash';

    const getCurrencySymbol = (locale, currency) => (0).toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/\d/g, '').trim();

    export default {
        // Required in Vue 3 so parent @change is the component emit, not a native
        // DOM listener — otherwise blur/click on the grid delivers an Event and
        // localUpdateFilterValue treats Event.type as filter criteria (CCDEV-18136).
        emits: ['change'],
        data: function () {
            return {
                rules: [{ condition: 'greaterThan', value: '', valueto: 60, combiner: undefined }],
            }
        },
        props: {
            item: Object,
            value: null,
        },
        created() {
            const self = this;
            this.updateReportDebouncer = _.debounce(function() {
                /*
                    Note the rules list is restricted to 1 or 2 items. We might allow more in the future, but then we
                    would need some way to indicate groups. But ag-grid did not support more than two, so we are probably ok.
                */
                console.log('debounce updateReport');

                // Match Vue2 / searchable text: empty value means no criteria — emit {} so
                // the filter stops reducing rows (back to "just added" state).
                if (self.rules.length < 1 || self.rules.some(a => (a.value === '' && a.condition != 'missing') || (a.condition == 'inRange' && a.valueto === ''))) {
                    self.$emit('change', {});
                    return;
                }

                if (self.rules.length == 1)
                    // Single rule scenario is the simplest format (made popular by ag-grid)
                    self.$emit('change', {
                        filterType: "number",
                        type: self.rules[0].condition,
                        filter: self.percentIsFraction ? self.rules[0].value / 100 : self.rules[0].value,
                        filterTo: self.percentIsFraction ? self.rules[0].valueto / 100 : self.rules[0].valueto,
                    });
                else
                    // Multiple rule scenario puts the conditions into an array
                    self.$emit('change', {
                        filterType: "number",
                        operator: self.rules[0].combiner,
                        conditions: self.rules.map(a => ({
                            type: a.condition,
                            filter: self.percentIsFraction ? a.value / 100 : a.value,
                            filterTo: self.percentIsFraction ? a.valueto / 100 : a.valueto,
                        }))
                    });
            }, 500);

            if (this.value && this.value.type) {
                this.rules = [{
                    condition: this.value.type,
                    value: this.percentIsFraction ? this.value.filter * 100 : this.value.filter,
                    valueto: this.percentIsFraction ? this.value.filterTo * 100 : this.value.filterTo,
                }];
            }
            else if (this.value && this.value.conditions && this.value.conditions.length == 2) {
                this.rules = [{
                    condition: this.value.conditions[0].type,
                    value: this.percentIsFraction ? this.value.conditions[0].filter * 100 : this.value.conditions[0].filter,
                    valueto: this.percentIsFraction ? this.value.conditions[0].filterTo * 100 : this.value.conditions[0].filterTo,
                    combiner: this.value.operator,
                },{
                    condition: this.value.conditions[1].type,
                    value: this.percentIsFraction ? this.value.conditions[1].filter * 100 : this.value.conditions[1].filter,
                    valueto: this.percentIsFraction ? this.value.conditions[1].filterTo * 100 : this.value.conditions[1].filterTo,
                }];
            }
        },
        watch: {
            rules: function (newv) {
                if (newv.every(a => a.value !== ''))
                    this.updateReport();
            },
            value: {
                handler(newValue) {
                    this.syncFromProps(newValue);
                },
                deep: true,
                immediate: true 
            },
        },
        computed: {
            // Platform-computed Percent columns (AI sentiment percentages) store a genuine 0-1
            // fraction. IVR Data Points of type Percent store whatever raw number the flow wrote,
            // unscaled, so they must not go through the /100 and *100 conversion below.
            percentIsFraction: function() {
                return this.item.SourceType == 'percentColumn' && !(this.item.Sources || []).includes('ivr_data_points');
            },
            condition_types: function () {
                return [
                    { text: this.Translate('Greater Than'), value: 'greaterThan' },
                    { text: this.Translate('Greater Than or Equal To'), value: 'greaterThanOrEqualTo' },
                    { text: this.Translate('Less Than'), value: 'lessThan' },
                    { text: this.Translate('Less Than or Equal To'), value: 'lessThanOrEqualTo' },
                    { text: this.Translate('Equals'), value: 'equals' },
                    { text: this.Translate('Does Not Equal'), value: 'notEquals' },
                    { text: this.Translate('Between'), value: 'inRange' },
                    { text: this.Translate('Missing'), value: 'missing' },
                ];
            },
            units: function() {
                return this.item.SourceType == 'durationColumn' ? 'sec' :
                    this.item.SourceType == 'percentColumn' ? '%' :
                        this.item.SourceType == 'currencyColumn' ? getCurrencySymbol(appSettings.LocaleKey, appSettings.Currency || 'USD') :
                            '#';
            },
            placeholder: function () {
                return this.item.SourceType == 'durationColumn' ? this.Translate('Enter value in seconds') :
                    this.item.SourceType == 'percentColumn' ? this.Translate('Enter percentage value') :
                        this.item.SourceType == 'currencyColumn' ? this.Translate('Enter currency value') :
                            this.Translate('Enter value');
            }
        },
        methods: {
            ...methods,
            isIncompleteRule(rule) {
                return (rule.value === '' && rule.condition != 'missing') || (rule.condition == 'inRange' && rule.valueto === '');
            },
            // CCDEV-18163: same draft-row guard as filter_searchableTextColumn — empty {} /
            // cleared re-asserts from the parent must not remove an incomplete Add Rule row.
            syncFromProps(value) {
                if (!value || Object.keys(value).length === 0 || value.cleared == 1 || (value.filterType === 'set' && (!value.values || value.values.length === 0))) {
                    if (this.rules.length > 0 && this.rules.some(a => this.isIncompleteRule(a)))
                        return;
                    if (this.rules.length > 0)
                        this.rules = [];
                }
            },
            updateReport() {
                console.log('updateReport');
                this.updateReportDebouncer();
            },
            addCondition() {
                if (this.rules.length === 1) {
                    this.rules[0].combiner = this.rules[0].combiner || 'AND';
                }
                this.rules.push({ condition: 'greaterThan', value: 0, valueto: 0, combiner: undefined });
                this.updateReport();
            },
            removeCondition() {
                this.rules.pop();
                this.updateReport();
            }
        }
    }
</script>

<style scoped>
    .verydense {
        max-height: 24px;
        min-height: 24px;
    }

    .mediumdense {
        max-height: 28px;
        min-height: 28px;
    }
    .customHoverButton:before {
        background-color: unset !important;
        transition: unset !important;
    }
    .customHoverButton {
        background-color:unset !important;
        color: var(--v-primary-base);
    }
    .customHoverButton:hover {
        filter: brightness(80%);
    }

    .aligned-spin-buttons :deep(input[type="number"]::-webkit-inner-spin-button) {
        transform: translateY(-5px);
    }

    .durationFilterPanel {
        padding: 8px 32px 12px 16px;
        margin-top: 4px;
    }

    .durationFilterPanel :deep(.q-field__label) {
        font-size: 14px;
    }

    .durationRuleRow {
        position: relative;
    }

    .durationRemoveBtn {
        position: absolute;
        right: -36px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;
    }

    .aligned-spin-buttons :deep(.q-field__label) {
        font-size: 14px;
    }

    .aligned-spin-buttons :deep(.q-field__native),
    .aligned-spin-buttons :deep(.q-field__input) {
        font-size: 16px;
    }

    .aligned-spin-buttons :deep(.q-field__append) span {
        font-size: 14px;
        margin-top: 2px;
    }

</style>
